import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import { Arrow } from 'icons/untitledui';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Document from 'documentDisplay/pages/DocumentPage';
import WarningDocument from 'documentDisplay/components/WarningDocument';
import * as Buttons from 'theme/components/Buttons.style';

// styles
import colours from 'theme/atoms/colours';
import * as Styled from 'documentDisplay/styles/Document.style';
import * as Text from 'theme/components/Text.style';

interface DocumentTranslation {
  language: string;
  parent: {
    id: string;
    use_source_url?: boolean;
  };
  page_numbers?: number[];
  publication_date?: string;
  source_url?: string;
  title: string;
  url: string;
  use_source_url?: boolean;
  has_form_template?: boolean;
  form_template_id?: string;
  form_template_name?: string;
}

interface DocumentListWithFormOptionProps {
  documentList: DocumentTranslation[];
  onFillForm: (document: DocumentTranslation) => void;
}

export default function DocumentListWithFormOption({
  documentList,
  onFillForm,
}: DocumentListWithFormOptionProps): JSX.Element {
  const { t } = useTranslation('pageDocumentDisplay');
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [pageResetCounter, setPageResetCounter] = useState<number>(0);
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    null
  );

  const getPageNumbers = (pageNumbers?: number[]): number[] => {
    if (!pageNumbers || pageNumbers.length === 0) {
      return [];
    }
    return pageNumbers;
  };

  const handlePageClick = (
    documentId: string,
    page: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setSelectedPage(page);
    setPageResetCounter((prev) => prev + 1);
    setExpandedAccordion(documentId);
  };

  const handleAccordionChange =
    (documentId: string) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordion(isExpanded ? documentId : null);
    };

  const handleFormFillClick = (document: DocumentTranslation) => {
    onFillForm(document);
  };

  const getReferenceType = (document: DocumentTranslation, index: number) => {
    if (document.source_url && document.parent.use_source_url) {
      return (
        <div>
          <Styled.ExternalLink
            href={document.source_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Arrow.ArrowUpRight size={16} />
            {document.title}
          </Styled.ExternalLink>
          {document.has_form_template && document.form_template_name && (
            <div style={{ marginTop: '10px' }}>
              <Buttons.Primary
                small
                onClick={() => handleFormFillClick(document)}
              >
                {t('documentDisplay.DocumentList.FillForm', {
                  formName: document.form_template_name,
                })}
              </Buttons.Primary>
            </div>
          )}
        </div>
      );
    }

    const documentId = `document-${index}`;
    return (
      <div>
        <Accordion
          expanded={expandedAccordion === documentId}
          onChange={handleAccordionChange(documentId)}
          sx={{
            boxShadow: 'none',
            backgroundColor: 'transparent',
            '&::before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<Arrow.ChevronRight size={18} />}
            sx={{
              backgroundColor: `${colours.neutral[100]}`,
              flexDirection: 'row-reverse',
              '& .MuiAccordionSummary-expandIconWrapper': {
                marginRight: '10px',
                '&.Mui-expanded': { transform: 'rotate(90deg)' },
              },
              padding: '0',
              '& .MuiAccordionSummary-content': { marginLeft: 0 },
            }}
          >
            <Styled.Row>
              <Text.P2Semibold>
                {document.title}
                {(() => {
                  const pageNumbers = getPageNumbers(document.page_numbers);
                  const shouldShowPageNumbers =
                    pageNumbers.length > 0 &&
                    !(pageNumbers.length === 1 && pageNumbers[0] === 1);
                  return (
                    <>
                      {shouldShowPageNumbers && ': '}
                      {shouldShowPageNumbers &&
                        pageNumbers.map((page) => (
                          <span key={page}>
                            <Styled.PageNumber
                              onClick={(e) =>
                                handlePageClick(documentId, page, e)
                              }
                            >
                              {t('documentDisplay.DocumentList.Page', {
                                page,
                              })}
                            </Styled.PageNumber>
                            {page !== pageNumbers[pageNumbers.length - 1] &&
                              ', '}
                          </span>
                        ))}
                    </>
                  );
                })()}
              </Text.P2Semibold>
            </Styled.Row>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0 0 0 25px' }}>
            <Document
              document={document}
              selectedPage={
                expandedAccordion === documentId ? selectedPage : null
              }
              resetCounter={pageResetCounter}
            />
          </AccordionDetails>
        </Accordion>
        {document.has_form_template && document.form_template_name && (
          <div style={{ marginTop: '10px', marginLeft: '25px' }}>
            <Buttons.Primary
              small
              onClick={() => handleFormFillClick(document)}
            >
              {t('documentDisplay.DocumentList.FillForm', {
                formName: document.form_template_name,
              })}
            </Buttons.Primary>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Text.P2Semibold style={{ marginBottom: '15px' }}>
        {t('documentDisplay.DocumentList.References')}
      </Text.P2Semibold>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {documentList.map((document, index) => (
          <div key={document.url}>
            {document.parent.id === 'warning-letter-doc' ? (
              <WarningDocument document={document} />
            ) : (
              getReferenceType(document, index)
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
